/// The permission model of specs/roles.md §3, mirrored from
/// services/orgs/src/utils/permissions.ts so the panel can decide what to
/// SHOW. The server decides what is ALLOWED; a mismatch here only ever hides a
/// button that would have worked or shows one the server refuses — and every
/// refusal is shown in words (domain/errors.ts).

export const MODULES = [
  'clients',
  'checkin',
  'sales',
  'cash',
  'passes',
  'schedule',
  'trainings',
  'stock',
  'payroll',
  'finance',
  'reports',
  'staff',
  'settings',
] as const
export type Module = (typeof MODULES)[number]

export const LEVELS = ['none', 'view', 'edit', 'manage'] as const
export type Level = (typeof LEVELS)[number]

export const SCOPES = ['own', 'branches', 'company'] as const
export type Scope = (typeof SCOPES)[number]

export type Permissions = Readonly<Record<Module, Level>>

/// `maxRefund` is kopecks; null = unlimited.
export type Limits = Readonly<{ maxDiscountPct: number | null; maxRefund: number | null }>

export type Profile = Readonly<{
  permissions: Permissions
  scope: Scope
  limits: Limits
  /// Owner-only actions — only the system `owner` role and gods.
  owner: boolean
}>

/// §3: finance and reports have no `edit` step ("—" in the table).
export const levelsFor = (module: Module): readonly Level[] =>
  module === 'finance' || module === 'reports' ? ['none', 'view', 'manage'] : LEVELS

export const levelRank = (level: Level): number => LEVELS.indexOf(level)
export const scopeRank = (scope: Scope): number => SCOPES.indexOf(scope)
export const atLeast = (level: Level, floor: Level): boolean => levelRank(level) >= levelRank(floor)

export const uniformPermissions = (level: Level): Permissions =>
  Object.fromEntries(MODULES.map((module) => [module, level])) as Record<Module, Level>

export const UNLIMITED: Limits = { maxDiscountPct: null, maxRefund: null }

/// Same rule as the server: limits only mean something at `sales: edit`.
export const effectiveLimits = (profile: Pick<Profile, 'permissions' | 'limits'>): Limits => {
  const sales = profile.permissions.sales
  if (sales === 'manage') return UNLIMITED
  if (sales === 'edit') return profile.limits
  return { maxDiscountPct: 0, maxRefund: 0 }
}

const limitAtMost = (a: number | null, b: number | null): boolean =>
  b === null ? true : a === null ? false : a <= b

/// What `a` has that `b` does not — module keys, `scope`, `maxDiscountPct`,
/// `maxRefund`, `owner`. The same list the server sends as `data.exceeds`.
export const exceeding = (a: Profile, b: Profile): readonly string[] => {
  const la = effectiveLimits(a)
  const lb = effectiveLimits(b)
  return [
    ...MODULES.filter((m) => levelRank(a.permissions[m]) > levelRank(b.permissions[m])),
    ...(scopeRank(a.scope) > scopeRank(b.scope) ? ['scope'] : []),
    ...(limitAtMost(la.maxDiscountPct, lb.maxDiscountPct) ? [] : ['maxDiscountPct']),
    ...(limitAtMost(la.maxRefund, lb.maxRefund) ? [] : ['maxRefund']),
    ...(a.owner && !b.owner ? ['owner'] : []),
  ]
}

export const atMost = (a: Profile, b: Profile): boolean => exceeding(a, b).length === 0
export const strictlyBelow = (a: Profile, b: Profile): boolean => atMost(a, b) && !atMost(b, a)

export const ALMIGHTY: Profile = {
  permissions: uniformPermissions('manage'),
  scope: 'company',
  limits: UNLIMITED,
  owner: true,
}
